import React, { useState } from 'react';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { ProfileSummary } from '../components/dashboard/ProfileSummary';
import { MatchList } from '../components/dashboard/MatchList';
import { OpportunityList } from '../components/dashboard/OpportunityList';
import { OpportunityForm } from '../components/dashboard/OpportunityForm';
import { MessagingPanel } from '../components/dashboard/MessagingPanel';
import { MeetingScheduler } from '../components/dashboard/MeetingScheduler';
import { ContractBuilder } from '../components/dashboard/ContractBuilder';
import { NotificationPrompt } from '../components/dashboard/NotificationPrompt';
import { ProfileEditor } from '../components/dashboard/ProfileEditor';
import { ProfilePresentation } from '../components/dashboard/ProfilePresentation';
import { MessageSquare, Calendar, FileText, Eye } from 'lucide-react';
interface DashboardProps {
  role: 'brand' | 'organizer';
  profileData: any;
  onBack?: () => void;
  onProfileUpdate?: (data: any) => void;
}
export function Dashboard({
  role,
  profileData,
  onBack,
  onProfileUpdate
}: DashboardProps) {
  const [view, setView] = useState<'overview' | 'add-opportunity'>('overview');
  const [opportunities, setOpportunities] = useState<any[]>([]);
  const [showMessages, setShowMessages] = useState(false);
  const [showMeetingScheduler, setShowMeetingScheduler] = useState(false);
  const [showContractBuilder, setShowContractBuilder] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showProfilePresentation, setShowProfilePresentation] = useState(false);
  const [viewingProfile, setViewingProfile] = useState<any>(null);
  const [currentProfile, setCurrentProfile] = useState(profileData);
  const handleAddOpportunity = (data: any) => {
    setOpportunities([...opportunities, data]);
    setView('overview');
  };
  const handleProfileSave = (data: any) => {
    const updatedProfile = {
      ...currentProfile,
      ...data
    };
    setCurrentProfile(updatedProfile);
    onProfileUpdate?.(updatedProfile);
    // Also update localStorage
    localStorage.setItem('sponsrai_user', JSON.stringify(updatedProfile));
  };
  const handleViewProfile = (profile: any) => {
    setViewingProfile(profile);
    setShowProfilePresentation(true);
  };
  const handleViewOwnProfile = () => {
    setViewingProfile(currentProfile);
    setShowProfilePresentation(true);
  };
  const accentBg = role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10';
  const accentText = role === 'brand' ? 'text-cyan-400' : 'text-purple-400';
  return <>
      <DashboardLayout role={role} onBack={onBack} onMessagesClick={() => setShowMessages(true)} unreadMessages={2} data-id="element-960">
        {view === 'overview' ? <div className="space-y-12" data-id="element-961">
            <ProfileSummary role={role} data={currentProfile || {
          name: 'Your Profile'
        }} onEdit={() => setShowProfileEditor(true)} data-id="element-962" />

            {/* Quick Actions */}
            <div className="grid md:grid-cols-4 gap-4" data-id="element-963">
              <button onClick={handleViewOwnProfile} className="bg-slate-800 border border-slate-700 hover:border-slate-600 rounded-xl p-5 text-left transition-colors group" data-id="element-964">
                <div className={`w-10 h-10 rounded-lg ${accentBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`} data-id="element-965">
                  <Eye size={20} className={accentText} data-id="element-966" />
                </div>
                <h3 className="font-bold text-white mb-1" data-id="element-967">Preview Profile</h3>
                <p className="text-sm text-slate-400" data-id="element-968">See how others see you</p>
              </button>

              <button onClick={() => setShowMessages(true)} className="bg-slate-800 border border-slate-700 hover:border-slate-600 rounded-xl p-5 text-left transition-colors group" data-id="element-969">
                <div className={`w-10 h-10 rounded-lg ${accentBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`} data-id="element-970">
                  <MessageSquare size={20} className={accentText} data-id="element-971" />
                </div>
                <h3 className="font-bold text-white mb-1" data-id="element-972">Messages</h3>
                <p className="text-sm text-slate-400" data-id="element-973">Chat with your matches</p>
              </button>

              <button onClick={() => setShowMeetingScheduler(true)} className="bg-slate-800 border border-slate-700 hover:border-slate-600 rounded-xl p-5 text-left transition-colors group" data-id="element-974">
                <div className={`w-10 h-10 rounded-lg ${accentBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`} data-id="element-975">
                  <Calendar size={20} className={accentText} data-id="element-976" />
                </div>
                <h3 className="font-bold text-white mb-1" data-id="element-977">Schedule Meeting</h3>
                <p className="text-sm text-slate-400" data-id="element-978">
                  Book time with partners
                </p>
              </button>

              <button onClick={() => setShowContractBuilder(true)} className="bg-slate-800 border border-slate-700 hover:border-slate-600 rounded-xl p-5 text-left transition-colors group" data-id="element-979">
                <div className={`w-10 h-10 rounded-lg ${accentBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`} data-id="element-980">
                  <FileText size={20} className={accentText} data-id="element-981" />
                </div>
                <h3 className="font-bold text-white mb-1" data-id="element-982">Create Contract</h3>
                <p className="text-sm text-slate-400" data-id="element-983">Define needs & offers</p>
              </button>
            </div>

            <OpportunityList opportunities={opportunities} role={role} onAdd={() => setView('add-opportunity')} data-id="element-984" />

            {opportunities.length > 0 && <MatchList role={role} onViewProfile={handleViewProfile} data-id="element-985" />}
          </div> : <div className="max-w-3xl mx-auto" data-id="element-986">
            <OpportunityForm role={role} onComplete={handleAddOpportunity} onCancel={() => setView('overview')} data-id="element-987" />
          </div>}
      </DashboardLayout>

      {/* Modals */}
      <MessagingPanel isOpen={showMessages} onClose={() => setShowMessages(false)} role={role} data-id="element-988" />

      <MeetingScheduler isOpen={showMeetingScheduler} onClose={() => setShowMeetingScheduler(false)} role={role} recipientName={viewingProfile?.name || 'Partner'} data-id="element-989" />

      <ContractBuilder isOpen={showContractBuilder} onClose={() => setShowContractBuilder(false)} role={role} partnerName={viewingProfile?.name || 'Partner'} data-id="element-990" />

      <ProfileEditor isOpen={showProfileEditor} onClose={() => setShowProfileEditor(false)} onSave={handleProfileSave} role={role} initialData={currentProfile} data-id="element-991" />

      <ProfilePresentation isOpen={showProfilePresentation} onClose={() => {
      setShowProfilePresentation(false);
      setViewingProfile(null);
    }} profile={viewingProfile} role={viewingProfile === currentProfile ? role : role === 'brand' ? 'organizer' : 'brand'} viewerRole={role} onMessage={() => {
      setShowProfilePresentation(false);
      setShowMessages(true);
    }} onSchedule={() => {
      setShowProfilePresentation(false);
      setShowMeetingScheduler(true);
    }} onContract={() => {
      setShowProfilePresentation(false);
      setShowContractBuilder(true);
    }} data-id="element-992" />

      {/* Notification Prompt */}
      <NotificationPrompt role={role} data-id="element-993" />
    </>;
}